<script>
import ApiService from "@/services/jobsiteTrackingService";
import Loading from "vue-loading-overlay";

const STEP_COLOURS = [
  { bg: "rgba(52,195,143,.15)", fg: "#0F7B5A", solid: "#34c38f" }, // green
  { bg: "rgba(241,180,76,.18)", fg: "#8A6000", solid: "#f1b44c" }, // amber
  { bg: "rgba(231,76,60,.12)", fg: "#B22222", solid: "#e74c3c" }, // red
  { bg: "rgba(80,72,229,.12)", fg: "#3A3496", solid: "#5048E5" }, // violet
];
const FALLBACK_COLOUR = {
  bg: "rgba(28,46,62,.10)",
  fg: "#1c2e3e",
  solid: "#1c2e3e",
};

export default {
  components: { Loading },
  props: {
    workOrderIdPublic: { type: String, default: null },
    workOrderStepIdPublic: { type: String, default: null },
    workOrderSteps: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      comments: [],
      newMessage: "",
      selectedWorkOrderStepIdPublic: null,
    };
  },
  computed: {
    availableSteps() {
      return this.workOrderSteps
        .filter((wos) => wos.workOrderIdPublic === this.workOrderIdPublic)
        .map((wos) => ({
          idPublic: wos.idPublic,
          name: wos.step.name,
          stepIdPublic: wos.step.idPublic,
        }));
    },
    stepColourByName() {
      const map = {};
      this.availableSteps.forEach((s, i) => {
        map[s.name] = STEP_COLOURS[i % STEP_COLOURS.length];
      });
      return map;
    },
    canSubmit() {
      return (
        this.newMessage.trim() !== "" &&
        this.selectedWorkOrderStepIdPublic !== null
      );
    },
  },
  watch: {
    workOrderIdPublic: {
      immediate: false,
      handler() {
        this.loadData();
      },
    },
    workOrderStepIdPublic(val) {
      // Sync default selection only if the user hasn't picked a tab yet
      // or the currently-picked tab is no longer available.
      const stillValid = this.availableSteps.some(
        (s) => s.idPublic === this.selectedWorkOrderStepIdPublic,
      );
      if (!stillValid) {
        this.selectedWorkOrderStepIdPublic = val;
      }
    },
    availableSteps(newSteps) {
      const stillValid = newSteps.some(
        (s) => s.idPublic === this.selectedWorkOrderStepIdPublic,
      );
      if (!stillValid) {
        this.selectedWorkOrderStepIdPublic =
          this.workOrderStepIdPublic ||
          (newSteps[0] && newSteps[0].idPublic) ||
          null;
      }
    },
  },
  methods: {
    loadData() {
      if (!this.workOrderIdPublic) {
        this.comments = [];
        return;
      }
      this.isLoading = true;
      this.hasError = false;
      ApiService.getComments(this.workOrderIdPublic)
        .then(
          (response) => {
            this.comments = response.data.data;
          },
          (error) => {
            window.console.error(error);
            this.hasError = true;
          },
        )
        .finally(() => {
          this.isLoading = false;
        });
    },
    submit() {
      if (!this.canSubmit || this.isLoading) return;
      this.isLoading = true;
      ApiService.createComment({
        message: this.newMessage,
        workOrderStepIdPublic: this.selectedWorkOrderStepIdPublic,
      })
        .then(
          (response) => {
            this.comments.unshift(response.data.data);
            this.newMessage = "";
          },
          (error) => {
            window.console.error(error);
          },
        )
        .finally(() => {
          this.isLoading = false;
        });
    },
    selectStep(wos) {
      this.selectedWorkOrderStepIdPublic = wos.idPublic;
    },
    colourForStepName(name) {
      return this.stepColourByName[name] || FALLBACK_COLOUR;
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name)
        .trim()
        .split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    formatDate(iso) {
      if (!iso) return "";
      return this.$dayjs(iso).format("DD-MM-YYYY");
    },
  },
  created() {
    // Seed the selected step from the prop default before the watcher
    // chain runs.
    this.selectedWorkOrderStepIdPublic = this.workOrderStepIdPublic;
    this.loadData();
  },
};
</script>
<template>
  <div class="notes">
    <template v-if="workOrderIdPublic">
      <!-- Compose card -->
      <div class="ccompose">
        <div class="ctabs" v-if="availableSteps.length > 0">
          <span
            v-for="wos in availableSteps"
            :key="wos.idPublic"
            class="ctab"
            :class="{ active: selectedWorkOrderStepIdPublic === wos.idPublic }"
            :style="
              selectedWorkOrderStepIdPublic === wos.idPublic
                ? 'background:' + colourForStepName(wos.name).solid +
                  ';border-color:' + colourForStepName(wos.name).solid +
                  ';color:white;'
                : ''
            "
            role="button"
            @click="selectStep(wos)"
            >{{ wos.name }}</span
          >
        </div>
        <textarea
          class="cinput"
          rows="2"
          v-model="newMessage"
          :placeholder="$t('general.addcomment')"
          v-on:keydown.enter.ctrl.exact.prevent="submit"
        />
        <div class="cfooter">
          <button class="sbtn" :disabled="!canSubmit" @click="submit">
            <i class="mdi mdi-send"></i>
            {{ $t("general.addcomment") }}
          </button>
        </div>
      </div>

      <!-- Comment list -->
      <div class="clist" v-if="comments.length > 0">
        <div
          class="ci"
          v-for="(c, idx) in comments"
          :key="c.idPublic || idx"
        >
          <div class="cih">
            <div class="cia">
              <div class="ciav">{{ initials(c.userName) }}</div>
              <div>
                <div class="cin">{{ c.userName }}</div>
                <div class="cid">{{ formatDate(c.created) }}</div>
              </div>
            </div>
            <span
              class="cibg"
              :style="
                'background:' + colourForStepName(c.stepName).bg +
                ';color:' + colourForStepName(c.stepName).fg + ';'
              "
              >{{ c.stepName }}</span
            >
          </div>
          <div class="citxt">{{ c.message }}</div>
        </div>
      </div>
      <div
        v-else-if="!isLoading && !hasError"
        class="notes-empty"
      >
        {{ $t("general.nocomments") }}
      </div>
      <div v-else-if="hasError" class="notes-empty error">
        {{ $t("general.errorcomments") }}
      </div>

      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        color="#f1b44c"
        :width="70"
        :height="70"
        loader="dots"
        :is-full-page="false"
      />
    </template>

    <div v-else class="notes-disabled">
      <i class="mdi mdi-comment-text-outline mdi-36px"></i>
      <div class="notes-disabled-msg">
        {{ $t("general.notes") }}
      </div>
      <div class="notes-disabled-hint">
        {{ $t("general.workorder") }}…
      </div>
    </div>
  </div>
</template>
<style scoped>
.notes {
  padding: 11px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  position: relative;
  max-height: 100%;
  overflow: auto;
}

/* Compose card */
.ccompose {
  background: white;
  border-radius: 11px;
  border: 1px solid #e0e5ea;
  padding: 10px 12px;
}
.ctabs {
  display: flex;
  gap: 5px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.ctab {
  padding: 3px 9px;
  border-radius: 13px;
  font-size: 10px;
  font-weight: 500;
  border: 1px solid #e0e5ea;
  cursor: pointer;
  background: white;
  color: #7a8a99;
  user-select: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.cinput {
  width: 100%;
  border: 1px solid #e0e5ea;
  border-radius: 8px;
  padding: 7px 9px;
  font-size: 12px;
  color: #1c2e3e;
  background: #f4f6f8;
  resize: none;
  outline: none;
  font-family: inherit;
}
.cinput:focus {
  border-color: #f1b44c;
  background: white;
}
.cfooter {
  display: flex;
  justify-content: flex-end;
  margin-top: 7px;
}
.sbtn {
  background: #f1b44c;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 6px 13px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}
.sbtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Comment list */
.clist {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.ci {
  background: white;
  border-radius: 11px;
  border: 1px solid #e0e5ea;
  padding: 10px 12px;
}
.cih {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
  gap: 8px;
}
.cia {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.ciav {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1b44c;
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cin {
  font-size: 11px;
  font-weight: 600;
  color: #1c2e3e;
}
.cid {
  font-size: 10px;
  color: #7a8a99;
  margin-top: 1px;
}
.cibg {
  padding: 2px 8px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}
.citxt {
  font-size: 12px;
  color: #1c2e3e;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Empty / disabled states */
.notes-empty {
  padding: 32px 16px;
  text-align: center;
  color: #7a8a99;
  font-size: 12px;
  background: white;
  border-radius: 11px;
  border: 1px dashed #e0e5ea;
}
.notes-empty.error {
  color: #e74c3c;
  border-color: #f9bdbd;
}
.notes-disabled {
  background: white;
  border-radius: 11px;
  border: 1px dashed #e0e5ea;
  padding: 32px 16px;
  text-align: center;
  color: #7a8a99;
}
.notes-disabled-msg {
  font-size: 14px;
  font-weight: 600;
  color: #1c2e3e;
  margin-top: 6px;
}
.notes-disabled-hint {
  font-size: 12px;
  margin-top: 4px;
}
</style>
